import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button,
  Chip,
  Divider,
  IconButton,
  Modal,
  Box,
  Typography,
  Grid,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { calculateAge, getGenotypeDisplayParts } from 'src/util/util';
import RadioInput from 'src/components/UI/input/RadioInput';
import TextInput from 'src/components/UI/input/TextInput';
import { CLINICAL_RECORD_DIALOG_WIDTH } from 'src/components/UI/dialog/CustomDialog';
import ClinicalNoteDeleteModal from '../diagnoses-menu/ClinicalNoteDeleteModal';
import { CONSTANTS } from 'src/components/Constants';
import VariantList from './VariantList';
import { useFreshEntity } from 'src/hooks/useFreshEntity';

// Build an NCBI Gene URL for a stored genotype. Pattern A (preferred): direct
// gene-page link by Entrez ID when `id` is in "NCBIGene:NNN" form. Pattern B
// (fallback): NCBI search by HGNC symbol — NCBI resolves it to the canonical
// gene page. Constructed at click time, never persisted.
const buildNcbiGeneUrl = (genotype) => {
  if (!genotype) return null;
  const id = String(genotype.id || '');
  if (id.startsWith('NCBIGene:')) {
    const num = id.slice('NCBIGene:'.length).trim();
    if (num) {
      return `https://www.ncbi.nlm.nih.gov/gene/${encodeURIComponent(num)}`;
    }
  }
  const { symbol } = getGenotypeDisplayParts(genotype);
  if (symbol) {
    const term = `${symbol}[Symbol] AND Homo sapiens[Organism]`;
    return `https://www.ncbi.nlm.nih.gov/gene/?term=${encodeURIComponent(
      term
    )}`;
  }
  return null;
};

const GenotypeModal = ({
  open,
  onClose,
  genotype,
  onSave,
  currentUserName = '',
  onDeleteGenotype,
  isDisabled,
  patientDob,
  staffMemberNames = [],
  fetchFreshEntity,
  onFresh,
  refetchKey,
  onShowHistory
}) => {
  const [clinicallyConfirmed, setClinicallyConfirmed] = useState(false);
  const [patientReported, setPatientReported] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [displayOnPedigree, setDisplayOnPedigree] = useState(true);
  const [dateRecorded, setDateRecorded] = useState('');
  const [recordedBy, setRecordedBy] = useState('');
  const [recordedAge, setRecordedAge] = useState('');
  const [carrierStatus, setCarrierStatus] = useState('');
  const [comment, setComment] = useState('');
  const [variants, setVariants] = useState([]);
  // Preserved on round-trip so already-saved ClinVar selections aren't lost
  // when the modal is closed. The picker UI itself was removed.
  const [selectedVariantIds, setSelectedVariantIds] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);

  // Tracks whether the clinician has touched any input on this modal
  // session — if so, we won't clobber their edits when the silent
  // background refetch from `useFreshEntity` resolves.
  const hasUserEditedRef = useRef(false);

  const { entity: liveGenotype, isFetching } = useFreshEntity({
    open,
    fetcher: fetchFreshEntity,
    fallback: genotype,
    onFresh,
    refetchKey
  });

  const yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  const computeAge = useCallback(
    (date) => {
      if (!patientDob || !date) return '';
      const parsedDate = new Date(date);
      if (Number.isNaN(parsedDate.getTime())) return '';
      return calculateAge(patientDob, parsedDate);
    },
    [patientDob]
  );

  useEffect(() => {
    if (!liveGenotype) return;
    if (hasUserEditedRef.current) return;
    setClinicallyConfirmed(liveGenotype.clinicallyConfirmed || false);
    setPatientReported(liveGenotype.patientReported || false);
    setIsPrivate(liveGenotype.isPrivate || false);
    setDisplayOnPedigree(liveGenotype.displayOnPedigree ?? true);
    setDateRecorded(liveGenotype.dateRecorded || '');
    setRecordedBy(liveGenotype.recordedBy || currentUserName);
    setCarrierStatus(liveGenotype.carrierStatus || '');
    setComment(liveGenotype.comment || '');
    // Always seed from the saved age. The sync effect below recomputes when
    // the user changes the date, but never overwrites a saved age because
    // no date was recorded.
    setRecordedAge(liveGenotype.recordedAge ?? '');
    setVariants(
      Array.isArray(liveGenotype.variants) ? liveGenotype.variants : []
    );
    setSelectedVariantIds(
      Array.isArray(liveGenotype.selectedClinVarIds)
        ? liveGenotype.selectedClinVarIds
        : []
    );
  }, [liveGenotype, currentUserName, patientDob, computeAge]);

  // Reset the dirty flag every time the modal closes so the next open
  // re-seeds cleanly.
  useEffect(() => {
    if (!open) hasUserEditedRef.current = false;
  }, [open]);

  const markDirty = () => {
    hasUserEditedRef.current = true;
  };

  useEffect(() => {
    if (!patientDob) return;
    const nextAge = computeAge(dateRecorded);
    // Only apply a date-derived age — never erase a saved age because the
    // date field is empty. The inline-capture path stores age without a
    // date, and we must preserve it on re-hydration.
    if (nextAge === '') return;
    setRecordedAge((prev) =>
      String(nextAge) === String(prev) ? prev : nextAge
    );
  }, [patientDob, computeAge, dateRecorded]);

  const handleClinicianInterpretationChange = useCallback(
    (variantId, value) => {
      hasUserEditedRef.current = true;
      setVariants((prev) =>
        prev.map((v) =>
          v.variantId === variantId
            ? {
                ...v,
                clinicianInterpretation: value,
                clinicianInterpretationBy: currentUserName || '',
                clinicianInterpretationAt: new Date().toISOString()
              }
            : v
        )
      );
    },
    [currentUserName]
  );

  // APPENDS the acknowledgement. Never edits or removes an earlier one: the
  // audit question is who stood behind what and when, so a reclassification by
  // a second clinician has to read as exactly that rather than as the first
  // opinion vanishing.
  //
  // The determination is applied HERE, from the dialog, so deciding and
  // reviewing are one act — and so Cancel in the dialog leaves nothing behind.
  // Each entry snapshots what was reviewed, which is what lets the marker
  // re-raise if any of the three values moves later.
  const handleReviewDiscrepancy = useCallback(
    (variantId, { determination, note } = {}) => {
      hasUserEditedRef.current = true;
      const at = new Date().toISOString();
      setVariants((prev) =>
        prev.map((v) =>
          v.variantId === variantId
            ? {
                ...v,
                clinicianInterpretation: determination,
                clinicianInterpretationBy: currentUserName || '',
                clinicianInterpretationAt: at,
                discrepancyReviews: [
                  ...(Array.isArray(v.discrepancyReviews)
                    ? v.discrepancyReviews
                    : []),
                  {
                    reviewedBy: currentUserName || '',
                    reviewedAt: at,
                    note: note || '',
                    determination,
                    labAtReview: v.labInterpretation || '',
                    clinvarAtReview: v.clinvarInterpretation || ''
                  }
                ]
              }
            : v
        )
      );
    },
    [currentUserName]
  );

  const handleSaveClick = () => {
    onSave({
      ...(liveGenotype || genotype),
      clinicallyConfirmed,
      patientReported,
      isPrivate,
      displayOnPedigree,
      dateRecorded,
      recordedBy,
      recordedAge: recordedAge === '' ? undefined : Number(recordedAge),
      carrierStatus,
      comment: comment?.trim() || undefined,
      variants,
      selectedClinVarIds: selectedVariantIds
    });
  };

  const handleOpenModal = () => {
    setOpenDeleteModal(true);
  };

  const handleCloseModal = () => {
    setOpenDeleteModal(false);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await onDeleteGenotype((liveGenotype || genotype).id);
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setDeleteLoading(false);
      handleCloseModal();
    }
  };

  return (
    <>
      <ClinicalNoteDeleteModal
        loading={deleteLoading}
        open={openDeleteModal}
        handleCloseModal={handleCloseModal}
        onConfirm={handleDelete}
        title="Are you sure you want to delete this Genotype ?"
        content="Warning: This action will permanently delete this Genotype. Once deleted, this
                  information will be permanently removed and cannot be restored."
      />
      <Modal open={open} onClose={onClose}>
        <Box
          data-va-anchor="pedigree.genotypes.editDialog"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            // Match the shared clinical-record / disease modal width so the
            // genotype editor reads as part of the same family of dialogs.
            width: CLINICAL_RECORD_DIALOG_WIDTH,
            maxWidth: '95vw',
            height: 700,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 1,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              backgroundColor: 'secondary.main',
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
              flexShrink: 0
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold', textAlign: 'left', color: '#fff' }}
            >
              Edit Genotype
              {isFetching && (
                <Typography
                  component="span"
                  sx={{ ml: 1, fontSize: 12, fontWeight: 400, opacity: 0.85 }}
                >
                  (refreshing…)
                </Typography>
              )}
            </Typography>
            <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 2, pt: 3, overflowY: 'auto', flex: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1,
                  mb: 0.5
                }}
              >
                <Typography variant="body1">
                  <strong>Symbol:</strong>{' '}
                  {getGenotypeDisplayParts(liveGenotype || genotype).symbol ||
                    'N/A'}
                </Typography>
                {(() => {
                  const target = liveGenotype || genotype;
                  const url = buildNcbiGeneUrl(target);
                  if (!url) return null;
                  const id = String(target?.id || '');
                  const label = id.startsWith('NCBIGene:') ? id : 'NCBI Gene';
                  return (
                    <Chip
                      label={label}
                      clickable
                      onClick={() =>
                        window.open(url, '_blank', 'noopener,noreferrer')
                      }
                      sx={{
                        fontSize: '1rem',
                        fontWeight: 400,
                        border: 'none',
                        color: '#1a7a8a',
                        backgroundColor: 'rgba(43, 169, 191, 0.15)',
                        borderRadius: '10px',
                        height: 'auto',
                        py: 0.5
                      }}
                    />
                  );
                })()}
              </Box>
              {getGenotypeDisplayParts(liveGenotype || genotype)
                .description && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 0.5
                  }}
                >
                  {
                    getGenotypeDisplayParts(liveGenotype || genotype)
                      .description
                  }
                </Typography>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Variants
              </Typography>
              <VariantList
                variants={variants}
                onClinicianInterpretationChange={
                  handleClinicianInterpretationChange
                }
                onReviewDiscrepancy={handleReviewDiscrepancy}
                reviewerName={currentUserName}
                onSourceTestClick={(detail) => {
                  window.dispatchEvent(
                    new CustomEvent('open-source-test-record', { detail })
                  );
                }}
                disabled={isDisabled}
              />
            </Box>

            {/* Date / Recorded By */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={6}>
                <TextInput
                  label="Date Recorded"
                  name="dateRecorded"
                  type="date"
                  value={dateRecorded}
                  onChange={(val) => {
                    markDirty();
                    setDateRecorded(val);
                    if (patientDob) {
                      const age = computeAge(val);
                      if (age !== '') setRecordedAge(age);
                    }
                  }}
                  disableFuture
                />
              </Grid>
              <Grid size={6}>
                <TextInput
                  label="Recorded By"
                  name="recordedBy"
                  type="autocomplete"
                  value={recordedBy}
                  options={staffMemberNames}
                  onChange={(newValue) => {
                    markDirty();
                    const label = newValue ? newValue.label || newValue : '';
                    setRecordedBy(label);
                  }}
                />
              </Grid>
              <Grid size={6}>
                <TextInput
                  label="Recorded Age"
                  name="recordedAge"
                  type="number"
                  value={recordedAge ?? ''}
                  // Disabled (muted) only when an age is actually being
                  // auto-calculated, i.e. DoB AND date recorded both set.
                  // Editable when either is missing so a known age can be
                  // recorded directly.
                  disabled={!!patientDob && !!dateRecorded}
                  onChange={
                    patientDob && dateRecorded
                      ? undefined
                      : (e) => {
                          markDirty();
                          const val = e.target.value;
                          const parsed = val === '' ? '' : Number(val);
                          if (val === '' || !Number.isNaN(parsed)) {
                            setRecordedAge(val === '' ? '' : parsed);
                          }
                        }
                  }
                />
              </Grid>
              <Grid size={6}>
                <TextInput
                  label="Carrier Status"
                  name="carrierStatus"
                  type="select"
                  value={carrierStatus}
                  onChange={(e) => {
                    markDirty();
                    setCarrierStatus(e.target.value);
                  }}
                  options={[
                    { label: 'None', value: '' },
                    ...CONSTANTS.carrierStatuses,
                    { label: 'Affected', value: 'Affected' }
                  ]}
                />
                {/* Guidance for values that need disambiguating — currently only
                    "Homozygote or Compound Heterozygote", now that the narrower
                    "Homozygote" also appears in the list (TGCLOUD-1655).
                    Rendered here rather than on the option itself for two reasons:
                    `label` is also what draws on the pedigree and shows in the
                    record, and SelectInput is a NATIVE select, so option text
                    appears in the closed field too — a hint folded into the label
                    would sit in the field permanently once selected. */}
                {CONSTANTS.carrierStatuses
                  .filter((o) => o.hint)
                  .map((o) => (
                    <Typography
                      key={o.value}
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        color: 'text.secondary'
                      }}
                    >
                      {o.label} — {o.hint}
                    </Typography>
                  ))}
              </Grid>
              <Grid size={6}>
                <RadioInput
                  label="Clinically Confirmed"
                  name="clinicallyConfirmed"
                  onChange={(e) => {
                    markDirty();
                    setClinicallyConfirmed(e.target.value);
                  }}
                  options={yesNoOptions}
                  value={clinicallyConfirmed}
                  row
                />
              </Grid>
              <Grid size={6}>
                <RadioInput
                  label="Patient Reported"
                  name="patientReported"
                  onChange={(e) => {
                    markDirty();
                    setPatientReported(e.target.value);
                  }}
                  options={yesNoOptions}
                  value={patientReported}
                  row
                />
              </Grid>
              <Grid size={6}>
                <RadioInput
                  label="Private"
                  name="isPrivate"
                  onChange={(e) => {
                    markDirty();
                    setIsPrivate(e.target.value);
                  }}
                  options={yesNoOptions}
                  value={isPrivate}
                  row
                />
              </Grid>
              <Grid size={6}>
                <RadioInput
                  label="Display on Pedigree"
                  name="displayOnPedigree"
                  onChange={(e) => {
                    markDirty();
                    setDisplayOnPedigree(e.target.value);
                  }}
                  options={yesNoOptions}
                  value={displayOnPedigree}
                  row
                />
              </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
              <TextInput
                type="textarea"
                label="Comments"
                name="comment"
                value={comment || ''}
                onChange={(e) => {
                  markDirty();
                  setComment(e.target.value);
                }}
                rows={3}
              />
            </Box>

            {/* Bottom action buttons */}
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleOpenModal}
                disabled={isDisabled || deleteLoading}
                endIcon={
                  deleteLoading ? <CircularProgress size={15} /> : undefined
                }
              >
                Delete
              </Button>
              <Button variant="outlined" color="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSaveClick}
                disabled={isDisabled}
              >
                Save
              </Button>
            </Box>
            {onShowHistory && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <IconButton
                  size="small"
                  title="Record history"
                  onClick={onShowHistory}
                >
                  <HistoryIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default GenotypeModal;
